import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  forwardRef,
  inject,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { merge, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { NavigationItemInterface } from '../models/navigation-item-interface';
import { NgClass, NgTemplateOutlet, NgStyle } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { TranslateDirective } from '@ngx-translate/core';
import { NavItemComponent } from '../nav-item/nav-item.component';
import { NavGroupComponent } from '../nav-group/nav-group.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-nav-collapsable',
  templateUrl: './nav-collapsable.component.html',
  styleUrls: ['./nav-collapsable.component.scss'],
  imports: [
    NgClass,

    NgTemplateOutlet,
    RouterLink,
    RouterLinkActive,
    MatIcon,
    TranslateDirective,
    NgStyle,

    NavItemComponent,
    // forwardRef breaks the circular ES module evaluation with NavGroupComponent
    forwardRef(() => NavGroupComponent),
  ],
})
export class NavCollapsableComponent implements OnInit, OnDestroy {
  @HostBinding('class')
  public classes = 'nav-collapsable nav-item';
  @HostBinding('class.open')
  public isOpen = false;
  @Input()
  public item: NavigationItemInterface;

  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _fuseNavigationService = inject(FuseNavigationService);
  private _fuseSidebarService = inject(FuseSidebarService);
  private _router = inject(Router);
  // Private
  private _unsubscribeAll: Subject<void>;

  /**
   * Constructor
   *
   * @param {ChangeDetectorRef} _changeDetectorRef
   * @param {FuseNavigationService} _fuseNavigationService
   * @param {FuseSidebarService} _fuseSidebarService
   * @param {Router} _router
   */
  public constructor() {
    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  /**
   * Collapse the collapsable navigation
   */
  public collapse(): void {
    if (!this.isOpen) {
      return;
    }

    this.isOpen = false;

    // Mark for check
    this._changeDetectorRef.markForCheck();

    this._fuseNavigationService.onItemCollapseToggled.next();
  }

  /**
   * Expand the collapsable navigation
   */
  public expand(): void {
    if (this.isOpen) {
      return;
    }

    this.isOpen = true;

    // Mark for check
    this._changeDetectorRef.markForCheck();

    this._fuseNavigationService.onItemCollapseToggled.next();
  }

  /**
   * Check if the given parent has the
   * given item in one of its children
   *
   * @param parent
   * @param item
   * @returns {boolean}
   */
  public isChildrenOf(parent, item): boolean {
    const children = parent.children;

    if (!children) {
      return false;
    }

    if (children.indexOf(item) > -1) {
      return true;
    }

    for (const child of children) {
      if (child.children) {
        if (this.isChildrenOf(child, item)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Check if the given url can be found
   * in one of the given parent's children
   *
   * @param parent
   * @param url
   * @returns {boolean}
   */
  public isUrlInChildren(parent, url): boolean {
    const children = parent.children;

    if (!children) {
      return false;
    }

    for (const child of children) {
      if (child.children) {
        if (this.isUrlInChildren(child, url)) {
          return true;
        }
      }

      if (child.url === url || url.startsWith(child.url + '/') || url.startsWith(child.url + '?')) {
        return true;
      }
    }

    return false;
  }

  /**
   * On destroy
   */
  public ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  /**
   * On init
   */
  public ngOnInit(): void {
    // Listen for router events
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll),
      )
      .subscribe((event: NavigationEnd) => {
        // Check if the url can be found in
        // one of the children of this item
        if (this.isUrlInChildren(this.item, event.urlAfterRedirects)) {
          this.expand();
        } else {
          this.collapse();
        }
      });

    // Listen for collapsing of any navigation item
    this._fuseNavigationService.onItemCollapsed.pipe(takeUntil(this._unsubscribeAll)).subscribe((clickedItem) => {
      if (clickedItem && clickedItem.children) {
        // Check if the clicked item is one
        // of the children of this item
        if (this.isChildrenOf(this.item, clickedItem)) {
          return;
        }

        // Check if the url can be found in
        // one of the children of this item
        if (this.isUrlInChildren(this.item, this._router.url)) {
          return;
        }

        // If the clicked item is not this item, collapse...
        if (this.item !== clickedItem) {
          this.collapse();
        }
      }
    });

    // Check if the url can be found in
    // one of the children of this item
    if (this.isUrlInChildren(this.item, this._router.url)) {
      this.expand();
    } else {
      this.collapse();
    }

    // Subscribe to navigation item
    merge(
      this._fuseNavigationService.onNavigationItemAdded,
      this._fuseNavigationService.onNavigationItemUpdated,
      this._fuseNavigationService.onNavigationItemRemoved,
    )
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        // Mark for check
        this._changeDetectorRef.markForCheck();
      });
  }

  /**
   * Toggle collapse
   *
   * @param ev
   */
  public toggleOpen(ev): void {
    ev.preventDefault();

    // If the collapsable component is already opened, and the sidebar folded and is opening,
    // the collapsable component doesn't need to toggle
    if (
      this.isOpen &&
      this._fuseSidebarService.getSidebar('navbar')!.folded &&
      !this._fuseSidebarService.getSidebar('navbar')!.wasUnfolded
    ) {
      return;
    }

    this.isOpen = !this.isOpen;

    // Navigation collapse toggled...
    this._fuseNavigationService.onItemCollapsed.next(this.item);
    this._fuseNavigationService.onItemCollapseToggled.next();
  }
}
